import { Injectable } from '@angular/core';
import { LocationRepository } from '@app/store/location.repository';
import { toZonedTime } from 'date-fns-tz';

export type TimeSegment = 'earlyMorning' | 'morning' | 'afternoon' | 'evening';

/**
 * Segments in chronological order, so the grouped time list reads 00:00 -> 23:59.
 */
export const TIME_SEGMENTS: readonly TimeSegment[] = [
  'earlyMorning',
  'morning',
  'afternoon',
  'evening',
];

/**
 * Minute-of-day boundaries (inclusive) evaluated against the property's
 * timezone — the same wall clock the guest sees on each time pill.
 */
const SEGMENT_RANGES: Record<TimeSegment, { start: number; end: number }> = {
  earlyMorning: { start: 0, end: 5 * 60 + 59 },
  morning: { start: 6 * 60, end: 11 * 60 + 59 },
  afternoon: { start: 12 * 60, end: 18 * 60 + 59 },
  evening: { start: 19 * 60, end: 23 * 60 + 59 },
};

@Injectable({
  providedIn: 'root',
})
export class TimeFilterService {
  constructor(private locationRepo: LocationRepository) {}

  filterTimesBySegment(
    dates: (Date | string | number)[],
    timeRange: TimeSegment,
  ): Date[] {
    const range = SEGMENT_RANGES[timeRange];

    if (!dates?.length || !range) {
      return [];
    }

    const timezone = this.locationRepo.getActiveLocation()?.timezone;

    return dates.filter((date) => {
      const minutes = this.minuteOfDay(date, timezone);
      return minutes >= range.start && minutes <= range.end;
    }) as Date[];
  }

  /**
   * Minutes elapsed since midnight for `date`. Without a property timezone the
   * device clock is used, which is also what the display pipe falls back to.
   */
  private minuteOfDay(
    date: Date | string | number,
    timezone: string | undefined,
  ): number {
    const parsed = new Date(date);
    const zoned = timezone ? toZonedTime(parsed, timezone) : parsed;
    return zoned.getHours() * 60 + zoned.getMinutes();
  }
}
